import { FC, useState } from 'react';
import { Button, Col, Row, Space, Typography } from 'antd';
import { TbDots, TbPencilCode } from 'react-icons/tb';

import { validateReportComponentEmbedUrl } from '@/hooks/reportComponents/embedValidation';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useReportComponentAccess } from '@/hooks/useNestedResourceAccess';
import {
    ReportComponentActionsDropdown,
    ReportComponentCommentButton,
    ReportComponentIcon,
    ReportComponentLockButton,
    SmallTooltip,
} from '@/shared';
import type { ReportComponentCommentResourceContext } from '@/shared/components/activity/ReportComponentCommentButton';
import { DragHandle } from '@/shared/components/drag-and-drop';
import { ReportComponent } from '@/types';

import EmptyEmbed from './components/EmptyEmbed';

interface EmbedReportComponentProps {
    commentResource?: ReportComponentCommentResourceContext;
    component: ReportComponent;
    readOnly?: boolean;
    transformScale?: string;
}

const EmbedReportComponent: FC<EmbedReportComponentProps> = ({
    commentResource,
    component,
    readOnly = false,
    transformScale = '0.75',
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const reportAccess = useReportComponentAccess(component);

    const [isEditting, setIsEditting] = useState(false);
    const [iframeRefreshKey, setIframeRefreshKey] = useState(0);

    const hasEmbedUrl = Boolean(component?.embedURL);
    const isValidEmbed = validateReportComponentEmbedUrl(component.type, component.embedURL).isValid;
    const showEditor = isEditting || !hasEmbedUrl || !isValidEmbed;
    const canModify = reportAccess.canUpdate && !component.locked && !readOnly;

    const [hovered, setHovered] = useState(false);

    return (
        <div
            style={{ height: '100%', border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Row style={{ padding: '0 0.5rem', height: '3rem' }} gutter={[8, 0]} align='middle'>
                {canModify && (
                    <Col className='rgl-drag-handle-slot' flex='none'>
                        <DragHandle
                            className='rgl-drag-handle'
                            label={`Move ${component.name ?? 'embedded report component'}`}
                        />
                    </Col>
                )}
                <Col flex='none'>
                    <ReportComponentIcon type={component.type} />
                </Col>
                <Col flex='none'>
                    <Typography.Title level={5} style={{ marginBottom: 0 }}>
                        {component?.name ?? 'Untitled Component'}
                    </Typography.Title>
                </Col>
                <Col flex='auto' />
                {!readOnly && <ReportComponentLockButton component={component} />}
                {hovered && !readOnly && (
                    <Col flex='none'>
                        <Space.Compact>
                            {reportAccess.canUpdate && (
                                <SmallTooltip title='Edit Embed URL' placement='topRight'>
                                    <Button disabled={!canModify} size='small' onClick={() => setIsEditting((v) => !v)}>
                                        <TbPencilCode style={{ color: token.colorTextTertiary }} />
                                    </Button>
                                </SmallTooltip>
                            )}
                            {reportAccess.canComment && (
                                <ReportComponentCommentButton
                                    commentResource={commentResource}
                                    component={component}
                                    tooltipPlacement='topRight'
                                />
                            )}
                            <ReportComponentActionsDropdown
                                component={component}
                                onRefresh={() => setIframeRefreshKey((key) => key + 1)}
                                refreshDisabled={!isValidEmbed}
                            >
                                <span>
                                    <SmallTooltip title='Component Actions' placement='topRight'>
                                        <Button size='small'>
                                            <TbDots style={{ color: token.colorTextTertiary }} />
                                        </Button>
                                    </SmallTooltip>
                                </span>
                            </ReportComponentActionsDropdown>
                        </Space.Compact>
                    </Col>
                )}
            </Row>
            <div
                style={{
                    margin: '0.5rem',
                    marginTop: 0,
                    border: `1px solid ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    height: 'calc(100% - 3.5rem)',
                    overflow: 'hidden',
                    background: token.colorBgBase,
                }}
            >
                {showEditor ? (
                    <EmptyEmbed
                        component={component}
                        transformScale={transformScale}
                        onDone={() => setIsEditting(false)}
                        disabled={!canModify}
                    />
                ) : (
                    <iframe
                        key={iframeRefreshKey}
                        title={`${component?.type} Embed`}
                        src={component?.embedURL}
                        referrerPolicy='strict-origin-when-cross-origin'
                        frameBorder='0'
                        scrolling='yes'
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                        allowFullScreen
                        loading='lazy'
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'block',
                            background: 'transparent',
                        }}
                    />
                )}
            </div>
        </div>
    );
};

export default EmbedReportComponent;
